import React, { useState, useEffect, useRef } from "react";
import { FaBars } from "react-icons/fa";
import { FiX, FiSun, FiMoon } from "react-icons/fi";
import { useLocation } from "react-router-dom";

const Sidebar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const sidebarRef = useRef(null);
    const location = useLocation();
    const [darkMode, setDarkMode] = useState(
        localStorage.getItem("theme") === "dark"
    );

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
        } else {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
        }
    }, [darkMode]);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(event.target) &&
                isOpen
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const handleLinkClick = () => {
        setIsOpen(false);
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const getPageTitle = () => {
        switch (location.pathname) {
            case '/': return 'Piyush Joshi';
            case '/resume': return 'Resume';
            case '/projects': return 'My Projects';
            case '/contactme': return 'Contact Me';
            default: return 'Piyush Joshi';
        }
    };

    return (
        <div className="relative block md:hidden">
            <div
                className={`fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-30 transition-opacity duration-300 ease-in-out ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                onClick={toggleMenu}
            ></div>

            {!isOpen && (
                <div className="fixed top-0 left-0 right-0 h-16 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md z-50 flex items-center justify-between px-4 border-b border-gray-100 dark:border-slate-800">
                    <div className="flex items-center">
                        <button
                            onClick={toggleMenu}
                            className="text-gray-800 dark:text-gray-200 p-2 mr-3 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors duration-300 focus:outline-none"
                            aria-label="Open menu"
                        >
                            <FaBars className="text-lg" />
                        </button>
                        <span className="text-lg font-bold text-gray-800 dark:text-gray-100 tracking-tight">{getPageTitle()}</span>
                    </div>
                    <button
                        onClick={() => setDarkMode(!darkMode)}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                        aria-label="Toggle Dark Mode"
                    >
                        {darkMode ? <FiSun className="text-yellow-400" size={22} /> : <FiMoon className="text-gray-600" size={22} />}
                    </button>
                </div>
            )}
            <div
                ref={sidebarRef}
                className={`fixed inset-y-0 left-0 z-40 bg-white dark:bg-slate-900 w-72 transform transition-transform duration-500 ease-in-out ${isOpen ? "translate-x-0" : "-translate-x-full"
                    } shadow-2xl border-r border-gray-200 dark:border-slate-800`}
            >
                <button
                    onClick={toggleMenu}
                    className="absolute top-4 right-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-300"
                >
                    <FiX className="text-2xl" />
                </button>

                <div className="flex flex-col items-center pt-16 pb-8">
                    <div className="relative flex flex-col justify-start text-gray-800 dark:text-gray-100 text-xl font-bold ">
                        <span>Piyush Joshi</span>
                        <div className="h-1 w-full bg-gray-800 dark:bg-gray-300 rounded-full"></div>
                    </div>
                </div>

                <div className="flex flex-col items-center py-4 space-y-2 px-4">
                    {[
                        { href: "/", label: "Home" },
                        { href: "/resume", label: "Resume" },
                        { href: "/projects", label: "Projects" },
                        { href: "/contactme", label: "Contact" },
                    ].map((link, index) => (
                        <a
                            key={index}
                            href={link.href}
                            onClick={handleLinkClick}
                            className={`relative w-full px-6 py-3 rounded-lg text-center transition-all duration-300 ${isActive(link.href)
                                ? "bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white border-l-2 border-gray-800 dark:border-gray-300"
                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white"
                                } font-medium text-base`}
                        >
                            {link.label}
                        </a>
                    ))}
                </div>

                <div className="absolute bottom-8 left-0 right-0 flex justify-center">
                    <div className="w-32 h-32 bg-gray-100 dark:bg-slate-800 rounded-full filter blur-xl"></div>
                </div>

                <div className="absolute bottom-8 left-0 right-0">
                    <div className="flex justify-center space-x-6">
                        <a href="https://www.linkedin.com/in/piyush-joshi-324778254/" target="_blank" rel="noopener noreferrer" className="text-[#0a66c2] hover:text-blue-700 transition-colors duration-300">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452z" />
                            </svg>
                        </a>
                        <a href="https://github.com/Piyush0049" target="_blank" rel="noopener noreferrer" className="text-gray-800 dark:text-gray-300 hover:text-gray-600 dark:hover:text-white transition-colors duration-300">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                            </svg>
                        </a>
                        <a href="https://x.com/PiyushJoshi0049" target="_blank" rel="noopener noreferrer" className="text-gray-800 dark:text-gray-300 hover:text-gray-600 dark:hover:text-white transition-colors duration-300">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
